<template>
    <component
        :is="listTypes[type]"
        :notification="content.read !== null && content.read !== undefined"
        :read="hasRead"
        :read-background="content.read_background"
        :image="content.image"
        :image-type="content.image_type"
        :is-video="content.is_video"
        :line="content.line"
        :line-clamp="content.line_clamp"
        :icon="content.icon"
        :badges="content.badges"
        :unread-count="badgeCount"
        :right-icon="rightIcon"
        :image-url="content.image_url"
        :avatar="content.avatar"
        :has-link="hasLink"
        :expands="content.expands"
        :border="content.border"
        :footer-avatar="content.footer_avatar"
        :is-emphasised="!!searchTerm"
        @click="onClick"
    >
        <template
            v-if="content.title"
            #title
        >
            <UIHtml
                :html="highlight(content.title, 'data.title')"
                class="prose leading-6 text-[length:inherit] text-inherit"
                style="overflow-wrap: anywhere;"
            />
        </template>
        <template
            v-if="content.subheading"
            #subheading
        >
            <UIHtml
                :html="highlight(content.subheading, 'data.subheading')"
                class="prose leading-5 text-[length:inherit] text-inherit"
                style="overflow-wrap: anywhere;"
            />
        </template>
        <template
            v-if="content.body"
            #body
        >
            <UIHtml
                :html="highlight(content.body, 'data.body')"
                class="prose leading-5 text-[length:inherit] text-inherit"
                style="overflow-wrap: anywhere;"
            />
        </template>
        <template
            v-if="content.subtitle"
            #subtitle
        >
            <UIHtml
                :html="highlight(content.subtitle, 'data.subtitle')"
                class="prose text-[length:inherit] text-inherit"
                style="overflow-wrap: anywhere;"
            />
        </template>
        <template
            v-if="content.right_text"
            #indicators
        >
            <UIHtml
                :html="content.right_text"
                class="inline prose text-[length:inherit] text-inherit"
            />
        </template>
    </component>
</template>

<script setup>
    import { useRouter } from 'vue-router'
    import { escapeRegExp, forEach, intersection } from 'lodash'
    import linkType from '@/constants/linkType'

    import UIList from '@/components/ui/ListItemLayouts/UIList.vue'
    import UISimple from '@/components/ui/ListItemLayouts/UISimple.vue'
    import UIGallery from '@/components/ui/ListItemLayouts/UIGallery.vue'
    import UIHtml from '@/components/ui/UIHtml.vue'
    import { disableAutomaticRightIconKey } from '@/keys'

    const disableAutomaticRightIcon = inject(disableAutomaticRightIconKey, false)

    const listTypes = {
        list: UIList,
        simple: UISimple,
        gallery: UIGallery,
    }

    const props = defineProps({
        config: Object,
        type: {
            type: String,
            default: 'list',
        },
        searchTerm: String,
        searchProperties: Array,
        isRemoteSearch: Boolean,
    })

    const router = useRouter()

    const moduleStore = useModuleStore()
    const moduleHelpers = useModuleHelpers()
    const badgeStore = useBadgeStore()

    const clicking = ref(false)

    const meta = computed(() => props.config.meta)
    const content = computed(() => props.config.data)

    // TODO: Improve check so that the component type is considered
    const getChildIds = (items) => {
        let ids = []
        forEach(items, (item) => {
            if (item.meta) {
                ids.push(item.meta.id)
            }
            if (item.link?.component?.items) {
                ids = ids.concat(getChildIds(item.link.component.items))
            }
        })
        return ids
    }

    const childIds = computed(() => {
        return getChildIds(get(props.config, 'link.component.items', []))
    })

    const hasLink = computed(() => {
        return !!get(props.config, 'link')
    })

    const isModal = computed(() => {
        return props.config.link.type === 'modal'
    })

    const hasLinkExternal = computed(() => {
        return [
            linkType.external,
            linkType.externalApp,
        ].includes(get(props.config, 'link.type'))
    })

    const hasLinkAction = computed(() => {
        return [
            linkType.apiAction,
        ].includes(get(props.config, 'link.type'))
    })

    const hasRead = computed(() => {
        return content.value.read || moduleStore.currentModulesReadItemIds.includes(meta.value.id)
    })

    const badgeCount = computed(() => {
        if (content.value.badge_url) {
            return badgeStore.getBadgeCountForUrl(content.value.badge_url)
        }

        // Handle grouped notices that show badge without using a badge url
        return content.value.unread_count - intersection(childIds.value, moduleStore.currentModulesReadItemIds).length
    })

    const rightIcon = computed(() => {
        if (content.value.right_icon) {
            return content.value.right_icon
        }

        if (!hasLink.value || hasLinkAction.value || disableAutomaticRightIcon || isModal.value) {
            return null
        }

        if (hasLinkExternal.value) {
            return {
                icon: 'arrow-square-out',
                icon_url: null,
                color: null,
            }
        }

        return {
            icon: 'caret-right',
            icon_url: null,
            color: null,
        }
    })

    const highlight = (html, listField) => {
        if (props.isRemoteSearch || !props.searchTerm || !props.searchProperties?.includes(listField)) {
            return html
        }
        const el = document.createElement('div')
        el.innerHTML = html
        const cleanText = el.textContent ?? el.innerText ?? ''
        const regex = new RegExp(escapeRegExp(props.searchTerm), 'ig')
        return cleanText.replace(regex, '<strong>$&</strong>')
    }

    const onClick = () => {
        if (clicking.value) {
            return
        }

        clicking.value = true
        moduleHelpers.handleClick(router, props.config, get(meta, ['value', 'id']))
            .catch((e) => {
                console.log('Failed to open link', e)
            })
            .finally(() => {
                clicking.value = false
            })
    }

    onMounted(() => {
        if (content.value.badge_url) {
            badgeStore.fetchUnsettled(content.value.badge_url)
        }
    })
</script>
